// Overhead
import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

// Components
import LinnerDiv from '../../components/lineDiv';
import Note from '../../components/note';
import ServerErrors from '../../components/ServerErrors';
import AmountWrapper from '../../components/amountWrapper';
import DonateHeader from '../../components/common/DonateHeader';
import CloseButton from '../../components/CloseButton';
import { Alert } from '@paypalcorp/pp-react-alert';

// App Store/Flux
import AppStore from '../../stores/AppStore';
import AppDispatcher from '../../dispatcher/AppDispatcher';
import * as DonateAppActions from '../../actions/AppActions';
import * as AppConstants from '../../utility/constants';
// Utility
import '../../utility/csrf';
import * as amountUtils from '../../components/amount/amountUtils';
import {
  handleTokenReuse,
  addShake,
  updateDonation,
  observeUntilEventObserved,
  getInitialDataInDom,
} from '../../utility/helper';
import templateData from '../../utility/getTemplateData';
import { formatAmount } from '../../utility/formator';
import DonateGiftAid from '../../guest-payment-components/DonateGiftAid';
import withDonate from '../../components/guest/WithDonate';
import withTheme from '../../components/withTheme';
import * as cpl from '../../utility/cpl';
import { CPL_PAGE_SPEC, FPTI_TAGS } from '../../fpti/fptiConstants';

import CancelAndReturn from '../../components/cancelAndReturn';
import Disclaimer from '../../components/Disclaimer';
import { PPGF_DISCLAIMER_TOUCHPOINT } from '../../utility/constants';
import {
  getPersonalizedMessages,
  hasPersonalizedMessages,
} from '../../utility/personalizedMessages';
import {
  scrollElementIntoView,
  objectOrArrayToQueryString,
  removeClass,
} from '../../utility/domUtils';
import {
  isExternalFlow,
  isCampaigns,
} from '../../utility/productIntegrationUtils';
import {
  isInContext,
  isMobileOrInContext,
  isMobileOrTablet,
} from '../../utility/inContextDonation';
import BackButton from '../../components/BackButton';
import { reactLazyPreload } from '../../utility/lazyLoader';
import FallbackSpinner from '../common/FallbackSpinner';
import { getParameterByName } from '../../utility/queryStringHelper';
import { isCrsAborted } from '../../utility/crsUtils';
import { updateGlobalFPTI } from '../../fpti/helper';
import AnonymousCheckbox from '../../components/anonymousDonation/anonymousCheckbox';
import { trackAnonymousCheckboxClick } from '../../fpti/fpti';
import withWorldReady from '../../components/withWorldReady';
import { Message } from '@paypalcorp/worldready-react';

const initialData = getInitialDataInDom();

const TeslaFormContainer = reactLazyPreload(() =>
  import(/* webpackChunkName: "TeslaFormContainer" */ './teslaFormContainer')
);

class Guest extends React.Component {
  constructor(props) {
    super(props);

    this.props.showErrorOnLoad();

    const anonymousStatusInStore = AppStore.getAnonymousStatus();
    const anonymousOptInStatus = !_isEmpty(anonymousStatusInStore)
      ? anonymousStatusInStore === 'checked'
      : templateData.anonymousStatus || false;

    this.state = {
      formattedAmount: this.getFormattedAmount(),
      hasLogo: templateData.hasLogo,
      subHeaderLabel: templateData.donationName,
      orgName: templateData.charityName,
      donationId: templateData.donationId,
      guestRedirectUrl: templateData.guestRedirectUrl,
      donateDisabled: false,
      showServiceErrors: false,
      errorMessages: [],
      recurringEnabled: templateData.recurringEnabled,
      recurringStatus: AppStore.getDonationRecurring() === 'checked' || false,
      country: initialData.selectedCountry,
      selectedCountry: initialData.selectedCountry,
      memberMandatory: templateData.memberMandatory || false,
      accountCreationTriggered: false,
      isDonating: false,
      isShipping: templateData.isShipping || false,
      shareAddress: {},
      progressiveComponentLoaded: false,
      paymentProcessing: false,
      programNames: templateData.donateContext.program_names,
      hasDisclaimer: hasPersonalizedMessages(PPGF_DISCLAIMER_TOUCHPOINT),
      showAuCrsBanner: false,
      showCaAmlBanner: false,
      anonymousEnabled: templateData.anonymousEnabled,
      anonymousOptInStatus,
      selectedRecurringFrequency: AppStore.getSelectedRecurringFrequency(),
    };
  }

  getFormattedAmount = () => {
    const currency = AppStore.getDonationCode() || '';
    const amount = AppStore.getDonationAmount();
    let formattedAmount = '';
    if (amount) {
      formattedAmount = formatAmount({ currency, value: amount });
    }
    return formattedAmount;
  };

  logCPLData = (logData) => {
    const spinnerOverlay = document.getElementById('spinnerOverlay');

    if (spinnerOverlay.style.display === 'none') {
      logData();
    } else {
      observeUntilEventObserved(
        spinnerOverlay,
        { attributes: true },
        (observer) => {
          logData();
          observer.disconnect();
          return;
        }
      );
    }
  };

  endCustomCPLTracking = () => {
    const cplSpec = CPL_PAGE_SPEC.TESLA_GUEST_FORM;
    if (
      document.referrer.includes('token=') ||
      performance.navigation.type === performance.navigation.TYPE_RELOAD
    ) {
      // on page refresh or country change
      window.onload = () => {
        setTimeout(() => {
          // Without these, loadEventStart and loadEventEnd would be 0
          // and thus tt would be a negative value
          // We need this because componentDidMount runs before
          // the page has completely loaded all content
          this.logCPLData(() => {
            cpl.logPerformance({
              ...cplSpec,
              uitype: 'page',
            });
          });
        }, 0);
      };
    } else {
      this.logCPLData(() => {
        window.PAYPAL.analytics.endCPLTracking({
          ...cplSpec,
          uitype: 'component',
        });
      });
    }
  };

  componentDidMount() {
    removeClass('#mainWrapper header.donate_global_nav', ['hide-header']);
    removeClass('#donateWrapper', ['is-landing']);
    this.props.toggleSpinner(true);
    this.setShouldShowAuCrsBanner();
    this.setShouldShowCaAmlBanner();

    // Checking token status
    handleTokenReuse();

    // Register for App Dispatcher Messages
    this.registerAppDispatcherMessages();

    // Show language selector in the footer
    DonateAppActions.showLanguageSelector(true);

    // startCPLTracking is initiated in landing page
    this.endCustomCPLTracking();
  }

  registerAppDispatcherMessages = () => {
    this.dispatcherMessages = AppDispatcher.register((payload) => {
      const actionType = _get(payload, 'action.actionType');

      if (actionType === 'CHANGE_AMOUNT') {
        const amount = _get(payload, 'action.item.amount', 0);
        this.updateAmount(amount);
      }
      if (actionType === AppConstants.CHANGE_RECURRING) {
        const recurringStatus =
          _get(payload, 'action.item.recurring', '') === 'checked';
        this.setState({ recurringStatus: recurringStatus });
      }
    });
  };

  componentWillUnmount() {
    AppDispatcher.unregister(this.dispatcherMessages);
  }

  getFPTDATA = () => {
    return {
      amount: AppStore.getDonationAmount(),
      code: AppStore.getDonationCode(),
      symbol: AppStore.getDonationSymbol(),
    };
  };

  updateConversionStatus = (status = false) => {
    this.setState({ donateDisabled: status });
  };

  /**
   * Display validation error
   */
  displayValidationError = () => {
    const errorMessage =
      amountUtils.getMinimumDonationErrorMessage('common.amount');
    this.setState({
      showValidationError: true,
      validationErrorMessage: errorMessage,
    });
    addShake();
  };

  hideValidationError = () => {
    this.setState({ showValidationError: false });
  };

  /**
   * Validates donation amount and displays the validation error if any
   */
  validateDonationAmount = () => {
    let validationResult =
      amountUtils.validateMinimumDonationAmount('common.amount');
    if (validationResult?.showValidationError) {
      this.setState(validationResult);
      addShake();
      scrollElementIntoView('.money_input');
      return false;
    }

    validationResult = amountUtils.validateMaximumDonationAmount();
    if (validationResult?.showValidationError) {
      this.setState(validationResult);
      addShake();
      scrollElementIntoView('.money_input');
      return false;
    }

    this.setState(validationResult);
    return true;
  };

  /**
   * Clear the validation error onFocus
   */
  handleAmountFocus = () => {
    if (this.state.showValidationError) {
      this.hideValidationError();
    }
  };

  /*
   * handlePaymentSuccess
   */
  handlePaymentSuccess = (response) => {
    const { token } = templateData;
    const queryParams = objectOrArrayToQueryString({ token });
    const isPending =
      response?.data?.confirmationData?.postBackData?.payment_status ===
      'Pending';
    const path = `/donate/guest/${
      response?.data?.confirmationData?.isOfacPending || isPending
        ? 'pending'
        : 'confirmation'
    }?${queryParams}`;

    return this.props.history.push(path);
  };

  /*
   * handlePaymentFailure
   */
  handlePaymentFailure = () => {
    this.props.toggleSpinner(false);
    return this.props.history.push(
      `/donate/error/payment?token=${templateData.token}`
    );
  };

  /*
   * handleMemberFailure
   * Redirect all member failure to donate/member
   * Known case
   * Case 1 :- WSM_PAYER_FUNDING_INSTRUMENTS_UNAVAILABLE
   * Case 2 :- WSM_VALIDATION_ERROR
   */
  handleMemberFailure = () => {
    updateDonation(`/donate/member?token=${templateData.token}`);
  };

  updateAmount(amount) {
    const currency = AppStore.getDonationCode() || '';
    this.setState({
      formattedAmount: formatAmount({ currency, value: amount }),
    });
    if (!this.state.recurringStatus && !this.state.memberMandatory) {
      this.setShouldShowCaAmlBanner();
    }
  }

  handleGiftAidOption(giftAidState) {
    DonateAppActions.updateGiftaidItFlag({ giftAidItFlag: giftAidState });
  }

  /**
   * Should show note when charityNote is enabled and recurring is disabled for non-client flows
   * @return {Boolean} true to show note field
   */
  shouldShowNote = () => {
    return (
      templateData.charityNoteEnabled &&
      !this.state.recurringStatus &&
      !isExternalFlow()
    );
  };

  createMarkup = (str) => {
    return {
      __html: str,
    };
  };

  getPersonalizedContent = () => {
    return getPersonalizedMessages(PPGF_DISCLAIMER_TOUCHPOINT, { merge: true });
  };

  setShouldShowAuCrsBanner = () => {
    const status = getParameterByName('status'),
      statusCode = getParameterByName('statusCode');

    if (isCrsAborted(status, statusCode)) {
      this.setState({
        showAuCrsBanner: true,
      });
      updateGlobalFPTI({
        [FPTI_TAGS.STATUS]: status,
        [FPTI_TAGS.STATUS_CODE]: statusCode,
      });
    }
  };

  setShouldShowCaAmlBanner = () => {
    if (templateData.enforceSenderCaAml && AppStore.getDonationAmount() > 999) {
      this.setState({
        showCaAmlBanner: true,
      });
    } else {
      this.setState({
        showCaAmlBanner: false,
      });
    }
  };

  handleAnonymousDonationChange = (status = false) => {
    this.setState({ anonymousOptInStatus: status });
    trackAnonymousCheckboxClick(status);
  };

  getAnonymousContent = () => {
    return {
      label:
        ['BR'].includes(this.state.country) && isCampaigns() ? (
          <Message id="guest.guestForm.anonymousCheckBox.br.label" />
        ) : (
          <Message id="guest.guestForm.anonymousCheckBox.label" />
        ),
      tooltip: <Message id="guest.guestForm.anonymousCheckBox.tooltip" />,
    };
  };

  render() {
    return (
      <>
        <div
          className={`${
            templateData.hasBanner ? 'gn_donate_wrapper' : 'donate_wrapper'
          } ${isMobileOrInContext() ? 'mobile_view' : ''} ${
            isInContext() ? 'context_gn_wrapper' : ''
          } ${
            !isInContext() &&
            isMobileOrTablet() &&
            !templateData.charityCancelUrl
              ? 'mobile_view_without_cancel_message'
              : ''
          }`}
        >
          {isExternalFlow() && templateData.ismobile && <CloseButton inverse />}
          {(isInContext() || isExternalFlow()) && (
            <BackButton inverse={templateData.hasBanner} />
          )}
          {this.state.showAuCrsBanner && (
            <Alert type="info" id="au_crs_info_banner">
              <Message id="guest.guestForm.auTax.abortedBanner" />
            </Alert>
          )}
          {this.state.showCaAmlBanner && (
            <Alert type="warning" id="ca_aml_warning_banner">
              <Message
                id="guest.guestForm.caAml.cipWarningBanner"
                maximumAllowedDonation={'$999 CAD'}
              />
            </Alert>
          )}
          <DonateHeader />
          <div className="container">
            <form
              id="guestForm"
              className={`${
                isExternalFlow() ? 'gn_donate_guest_form' : 'donate_guest_form'
              }`}
              noValidate
            >
              <AmountWrapper
                conversionStatus={this.updateConversionStatus}
                validateDonationAmount={this.validateDonationAmount}
                showValidationError={this.state.showValidationError}
                hideValidationError={this.hideValidationError}
                validationErrorMessage={this.state.validationErrorMessage}
                onFocus={this.handleAmountFocus}
                selectedRecurringFrequency={
                  this.state.selectedRecurringFrequency
                }
              />

              {this.state.anonymousEnabled && (
                <AnonymousCheckbox
                  content={this.getAnonymousContent()}
                  optInStatus={this.state.anonymousOptInStatus}
                  handleAnonymousDonationChange={
                    this.handleAnonymousDonationChange
                  }
                />
              )}

              {templateData.isGiftaiditEnabled && (
                <div
                  style={
                    this.state.recurringStatus ? { marginTop: '20px' } : {}
                  }
                >
                  <DonateGiftAid
                    isAlreadyEnrolled={false}
                    showAddress={false}
                    addressData="" // eslint-disable-line
                    isChecked={false}
                    handleGiftAidOption={this.handleGiftAidOption}
                    giftaidInfo={{
                      orgName: templateData.donationName ?? '',
                      amount: AppStore.getDonationAmount() || 0,
                      currency: AppStore.getDonationSymbol(),
                      donationCode: AppStore.getDonationCode(),
                      donateGiftPercentage: AppStore.getGiftAidItExtraAmount(),
                    }}
                  />
                </div>
              )}
              <LinnerDiv />
              {this.shouldShowNote() ? <Note /> : null}
              <ServerErrors />
            </form>
            <Suspense fallback={<FallbackSpinner />}>
              <TeslaFormContainer
                displayValidationError={this.displayValidationError}
                validateDonationAmount={this.validateDonationAmount}
                handlePaymentSuccess={this.handlePaymentSuccess}
                handlePaymentFailure={this.handlePaymentFailure}
                handleMemberFailure={this.handleMemberFailure}
              />
            </Suspense>
            {this.state.hasDisclaimer && (
              <Disclaimer content={this.getPersonalizedContent()} />
            )}
          </div>
        </div>
        <CancelAndReturn isGuest={true} />
      </>
    );
  }
}

Guest.propTypes = {
  toggleSpinner: PropTypes.func,
  history: PropTypes.object,
  showErrorOnLoad: PropTypes.func,
};

export default withRouter(withWorldReady(withDonate(withTheme(Guest))));
